import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface AIToolCatalogRow {
  id: string;
  name: string;
  category: string | null;
  vendor: string | null;
  description: string | null;
  full_description: string | null;
  logo_url: string | null;
  website_url: string | null;
  rating: number | null;
  trending: boolean | null;
  featured: boolean | null;
  pricing: string | null;
  level: number | null;
  
  tags: string[] | null;
  use_cases: string[] | null;
  pros: string[] | null;
  cons: string[] | null;
  integrations: string[] | null;
  founded: string | null;
  company: string | null;
}

const QUERY_KEY = ['ai-tools-catalog'];

async function fetchTools(): Promise<AIToolCatalogRow[]> {
  const { data, error } = await supabase
    .from('ai_tools_catalog')
    .select('*')
    .order('name');
  if (error) throw error;

  // Cast jsonb arrays to string arrays
  return (data ?? []).map((row: any) => ({
    ...row,
    use_cases: row.use_cases ?? [],
    pros: row.pros ?? [],
    cons: row.cons ?? [],
    integrations: row.integrations ?? [],
    
    tags: row.tags ?? [],
  }));
}

export function useAIToolsCatalog() {
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: fetchTools,
  });
}

// --- Admin mutations ---

interface ToolInput {
  name: string;
  category?: string;
  vendor?: string;
  description?: string;
  full_description?: string;
  website_url?: string;
  logo_url?: string;
  rating?: number;
  trending?: boolean;
  featured?: boolean;
  pricing?: string;
  level?: number;
  
  tags?: string[];
  use_cases?: string[];
  pros?: string[];
  cons?: string[];
  integrations?: string[];
  founded?: string;
  company?: string;
}

export function useCreateTool() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ToolInput) => {
      const { error } = await supabase.from('ai_tools_catalog').insert(input as any);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });
}

export function useUpdateTool() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...input }: ToolInput & { id: string }) => {
      const { error } = await supabase.from('ai_tools_catalog').update(input as any).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });
}

export function useDeleteTool() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('ai_tools_catalog').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });
}
